import { Card, CardContent } from "@/components/ui/card";
import { OverallProgress } from "./progress/OverallProgress";
import { LearningRecommendations } from "./progress/LearningRecommendations";
import { CategoryProgress } from "./progress/CategoryProgress";
import { ScoreHistory } from "./progress/ScoreHistory";
import { AchievementBadges } from "./progress/AchievementBadges";
import { useTestResults } from "@/hooks/useTestResults";
import { usePerformanceMetrics } from "@/hooks/usePerformanceMetrics";
import {
  calculateAverageScore,
  processChartData,
  calculateCategoryScores,
  generateAchievements,
} from "@/utils/testDataProcessing";

interface TestProgressProps {
  childId: string;
}

export const TestProgress = ({ childId }: TestProgressProps) => {
  const { data: testResults, isLoading } = useTestResults(childId);
  const { data: performanceMetrics } = usePerformanceMetrics(childId, testResults);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-gray-600">Loading progress data...</p>
        </CardContent>
      </Card>
    );
  }

  if (!testResults?.length) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-gray-600">No test results available yet</p>
        </CardContent>
      </Card>
    );
  }

  // Filter test results to only keep the latest attempt for each test
  const latestTestResults = testResults.reduce((acc: typeof testResults, result) => {
    const existingIndex = acc.findIndex(r => r.test_id === result.test_id);
    if (existingIndex === -1) {
      acc.push(result);
    } else if (new Date(result.created_at) > new Date(acc[existingIndex].created_at)) {
      acc[existingIndex] = result;
    }
    return acc;
  }, []);

  const averageScore = calculateAverageScore(latestTestResults);
  const chartData = processChartData(latestTestResults);
  const categoryScores = calculateCategoryScores(latestTestResults);
  const achievements = generateAchievements(latestTestResults);

  return (
    <div className="space-y-6">
      <OverallProgress averageScore={averageScore} />
      <CategoryProgress categoryScores={categoryScores} />
      <AchievementBadges achievements={achievements} />
      <LearningRecommendations recommendations={performanceMetrics?.suggestions || []} />
      <ScoreHistory chartData={chartData} />
    </div>
  );
};
